import { useState, useEffect } from 'react'

import gSession from '../script/session';
import gUtilities from '../script/utilities';
import gDataStore from "../script/data_store.js";

import TableWidget from './TableWidget';

const JobFilePage = () => {
    const empty = gUtilities.convertDbResultToTableData([]);
    const [job, setJob] = useState(null);
    const [files, setFiles] = useState(empty);
    const [bucket, setBucket] = useState(empty);
    const [banner, setBanner] = useState([{
        fingerprint: "No job",
        plan_name: "No job",
        state: "No job",
        machine_name: "No job",
        simulator_name: "No job"
    }]);
    const [refresh, setRefresh] = useState(true);

    let currentJob = gDataStore.retrieve("CurrentJob", null);
    if (!job) {
        setJob(currentJob);
        setRefresh(true);
        updateBanner(currentJob);
    } else if (currentJob && currentJob.job_id !== job.job_id) {
        setJob(currentJob);
        setRefresh(true);
        updateBanner(currentJob);
    }

    function onCurrentJob(currentJob) {
        setJob(currentJob);
        setRefresh(true);

        updateBanner(currentJob);
    }
    gDataStore.onStore("CurrentJob", onCurrentJob);

    const banner_columns = ["fingerprint", "plan_name", "state", "machine_name", "simulator_name"];
    const banner_titles = ["Fingerprint", "Plan Name", "State", "Machine", "Simulator"];
    const file_columns = ["filename", "tag"];
    const file_titles = ["Filename", "Tag"];

    function updateBanner(job) {
        setBanner([{
            fingerprint: gUtilities.fingerprint(job.job_id),
            plan_name: job.plan_name,
            state: job.state,
            machine_name: job.machine_name,
            simulator_name: job.simulator_name
        }]);
    }

    const k_file_order = [
        ".log",
        ".txt",
        ".png",
        ".jpg",
        ".xml",
        ".json",
        ".sh",
        ".dcm",
        ".h5"
    ];

    function GenerateFileTable(keys) {
        let items = [];

        keys.forEach(key => {
            items.push({
                filename: __removeKeyPrefix(key.value),
                key: key.value,
                tag: key.tag
            })
        });

        let sorted = items.sort((l, r) => {
            let il = k_file_order.length;
            let ir = k_file_order.length;
            for (let i = 0; i < k_file_order.length; i++) {
                if (l.filename.endsWith(k_file_order[i])) {
                    il = i;
                }
                if (r.filename.endsWith(k_file_order[i])) {
                    ir = i;
                }
            }

            if (il < ir) {
                return -1;
            } else if (il > ir) {
                return 1;
            }

            let matchl = l.filename.match(/beam-(\d)/);
            let matchr = r.filename.match(/beam-(\d)/);

            let bl = matchl ? parseInt(matchl[1]) : 0;
            let br = matchr ? parseInt(matchr[1]) : 0;

            if (bl < br) {
                return -1;
            } else if (bl > br) {
                return 1;
            }

            if (l.filename < r.filename) {
                return -1;
            } else if (l.filename > r.filename) {
                return 1;
            }

            return 0;
        });

        setFiles(sorted);
    }

    const load = async () => {
        let request = {
            service: "db.query",
            collection: "dcs_job",
            many: false,
            query: {
                job_id: job.job_id
            }
        };

        let response;
        try {
            response = await gSession.exchange(request);
            if (response.isError) {
                gUtilities.showError("Job File List", response.message);
            } else {
                GenerateFileTable(response.files.keys);
                setBucket(response.files.bucket);
            }
        } catch (ex) {
            gUtilities.showError("Job File List", ex.toString());
        }
    }

    function __keyPrefix() {
        return `job/${job.job_id}`;
    }

    function __removeKeyPrefix(key) {
        let prefix = __keyPrefix()
        let exp = `^${prefix}/(.+)$`;

        let match = key.match(exp);
        if (match) {
            return match[1];
        }

        return key;
    }

    useEffect(() => {
        setRefresh(false);
        if (refresh && job) {
            load();
        }

    }, [refresh]);

    async function getOpenMcPlotMetadata(data) {
        let request = {
            command: "agent.file-json",
            job_id: job.job_id,
            filename: data.filename.substring(0, data.filename.length - 4) + ".json"
        };

        let response = await gSession.exchange(request);
        if (response.isError) {
            response.origin = "???";
            response.basis = "???";
        }

        return response;
    }

    async function onView(data) {
        let request = {
            command: "agent.file-contents",
            job_id: job.job_id,
            bucket: bucket,
            key: data.key    // `${__keyPrefix()}/${data.filename}`
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("View File", response.message);
            return;
        }

        let url;
        if (currentJob.simulator_name === "openmc" && data.filename.endsWith(".png")) {
            let metadata = await getOpenMcPlotMetadata(data);

            let html = `
            <html>
                <body style="background: black; color: white;">
                    <div style="margin: auto; width: 80%; display: flex; padding-top: 20px">
                        <div style="padding: 10px">
                            <table>
                                <tr><td>DCS JOB ID</td><td>${job.job_id}</td></tr>
                                <tr><td>PLOT NAME</td><td>${data.filename}</td></tr>
                                <tr><td>PLOT BASIS</td><td>${metadata.basis}</td></tr>
                                <tr><td>PLOT ORIGIN</td><td>${metadata.origin}</td></tr>
                            </table>
                            </div>
                            <div>
                                <img src="data:image/png;charset=utf-8;base64,${response.contents}" alt="Red dot" style="width: 400; height: 400;"/>
                            </div>
                            <p></p>
                        </div>
                    </div>
                </body>
            </html>`;

            url = URL.createObjectURL(
                new Blob([html], { type: "text/html" })
            );
        } else if (data.filename.endsWith(".html")) {
            let buffer = atob(response.contents);
            let html = buffer.toString("utf-8");

            url = URL.createObjectURL(
                new Blob([html], { type: "text/html" })
            );
        } else if (data.filename.endsWith(".pdf")) {
            const blob = base64ToBlob(response.contents, 'application/pdf');
            url = URL.createObjectURL(blob);

            function base64ToBlob(base64, type = "application/octet-stream") {
                const binStr = atob(base64);
                const len = binStr.length;
                const arr = new Uint8Array(len);
                for (let i = 0; i < len; i++) {
                    arr[i] = binStr.charCodeAt(i);
                }
                return new Blob([arr], { type: type });
            }

        } else {
            let buffer = atob(response.contents);
            let text = buffer.toString("utf-8");

            if (data.filename.endsWith(".json")) {
                let obj = JSON.parse(text);
                text = JSON.stringify(obj, null, 4);
            }

            url = URL.createObjectURL(
                new Blob([text], { type: "text/plaintext" })
            );
        }


        window.open(url, "_blank", "noopener noreferrer");
    }

    async function onDownload(data) {
        let request = {
            command: "agent.s3_download_url",
            key: data.key
        };

        let response = await gSession.exchange(request);

        if (response.isError) {
            gUtilities.showError("Download File", response.message);
            return;
        }

        // will download file
        window.open(response.url);
    }

    function logRowClass(row) {
        console.log("ROW:", row);
        if (row && row.filename.endsWith(".log")) {
            return "table-info";
        } else if (row && row.filename.indexOf("openmc-") > 0 && row.filename.endsWith("-output.txt")) {
            return "table-success";
        } else if (row && row.filename.indexOf("openmc-") > 0 && row.filename.endsWith("-error.txt")) {
            return "table-danger";
        } else {
            return "table-light";
        }
    }

    function fileViewStyle(button, data) {
        let style;
        let filename = data.filename;

        let isViewable = filename.endsWith(".sh") ||
            filename.endsWith("inp") ||
            filename.endsWith(".log") ||
            filename.endsWith(".txt") ||
            filename.endsWith(".inp") ||
            filename.endsWith(".csv") ||
            filename.endsWith(".pdf") ||
            filename.endsWith(".xml") ||
            filename.endsWith(".jpg") ||
            filename.endsWith(".png") ||
            filename.endsWith(".json") ||
            filename.endsWith(".html") ||
            filename.endsWith(".plotm");

        if (isViewable) {
            style = { visiblity: "visible" };
        } else {
            style = { visibility: "hidden" };
        }

        return style;
    }

    let buttons = [
        {
            label: "View",
            callback: onView,
            style: fileViewStyle
        }
    ];

    return (
        <div>
            <div className="alert alert-dismissible alert-warning">
                <h4 className="alert-heading">Job Files</h4>
                <TableWidget name="Banner" data={banner} columns={banner_columns} titles={banner_titles} />
            </div>
            <div>
                <TableWidget data={files} columns={file_columns} titles={file_titles} buttons={buttons} rowClassFunction={logRowClass} />
            </div>
        </div>
    );
}

export default JobFilePage;